import { useCallback, type FC } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { TablePagination, useTableSort } from '@/ui';

import { resolveApiErrorMessage } from '../../lib/apiError';
import { useProductGroups, useProductsList } from './api/queries';
import { ProductsFilters } from './ProductsFilters';
import { ProductsTable } from './ProductsTable';
import {
  PAGE_SIZE,
  formatSort,
  resolveProductsSearch,
  type ActivityFilter,
  type KindFilter,
  type ProductsSearch,
} from './lib/searchParams';

// У маршрута два имени: идентификатор в дереве файлов (нужен useSearch, чтобы
// вывести тип search params) и путь в адресной строке (нужен navigate).
const ROUTE_ID = '/_app/goods' as const;
const ROUTE_PATH = '/goods' as const;

/*
 * Справочник товара - единственный экран на живом API (GET /api/Products).
 *
 * Фильтры, сортировка и страница читаются из search params маршрута и туда же
 * пишутся: страница сама состояния не держит, а перевыпускает запрос при смене
 * адреса.
 */
export const ProductsPage: FC = () => {
  const navigate = useNavigate();
  const search = useSearch({ from: ROUTE_ID });
  const resolved = resolveProductsSearch(search);

  const setSearch = useCallback(
    (patch: Partial<ProductsSearch>, resetPage = true) => {
      void navigate({
        to: ROUTE_PATH,
        search: (previous: ProductsSearch) => ({
          ...previous,
          ...patch,
          ...(resetPage ? { page: undefined } : null),
        }),
      });
    },
    [navigate],
  );

  const { toggleSort } = useTableSort(resolved.sort, (next) =>
    setSearch({ sort: formatSort(next) }),
  );

  const { data, isPending, isError, error } = useProductsList(resolved);
  const groups = useProductGroups(resolved.activity);

  // Числа приходят как number | string: бэкенд принимает числа и строками
  // (JsonSerializerDefaults.Web), и это честно отражено в спеке.
  const total = Number(data?.totalRecords ?? 0);
  const totalPages = Math.max(1, Number(data?.totalPages ?? 1));
  const products = data?.items ?? [];

  const firstOnPage = total === 0 ? 0 : (resolved.page - 1) * PAGE_SIZE + 1;
  const lastOnPage = Math.min(resolved.page * PAGE_SIZE, total);

  return (
    <>
      <ProductsFilters
        search={resolved.search}
        kind={resolved.kind}
        group={resolved.group}
        groups={groups.data ?? []}
        activity={resolved.activity}
        onSearchChange={(value) => setSearch({ search: value || undefined })}
        onKindChange={(value: KindFilter) =>
          setSearch({ kind: value === 'all' ? undefined : value })
        }
        onGroupChange={(value) => setSearch({ group: value || undefined })}
        onActivityChange={(value: ActivityFilter) =>
          setSearch({ activity: value === 'active' ? undefined : value })
        }
      />

      {isError ? (
        <div
          role="alert"
          className="rounded-lg border border-st-delayed-bg bg-st-delayed-bg px-4 py-3.5 text-[13px] text-st-cancelled"
        >
          Не удалось загрузить справочник: {resolveApiErrorMessage(error)}.
          Проверьте, что бэкенд запущен на порту 5080.
        </div>
      ) : (
        <ProductsTable
          products={products}
          isLoading={isPending}
          sort={resolved.sort}
          onSortChange={toggleSort}
          emptyLabel={
            resolved.search === ''
              ? 'В справочнике пока нет позиций'
              : `По запросу «${resolved.search}» ничего не найдено`
          }
          footer={
            <TablePagination
              page={resolved.page}
              totalPages={totalPages}
              onPageChange={(page) =>
                setSearch({ page: page === 1 ? undefined : page }, false)
              }
              summary={
                total === 0
                  ? 'Ничего не найдено'
                  : `Показаны ${firstOnPage}-${lastOnPage} из ${total}`
              }
            />
          }
        />
      )}
    </>
  );
};
